import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { audiencesService } from '../services';
import type {
  AudienceImportPreviewResponse,
  AudienceImportResponse,
} from '@troia-v3/types';

export const useImportPreview = (
  audienceId: string | undefined
): UseMutationResult<AudienceImportPreviewResponse, Error, File> => {
  return useMutation({
    mutationFn: (file: File) => audiencesService.importPreview(audienceId!, file),
  });
};

export interface StartImportArgs {
  file: File;
  identifierColumn: string;
  nameColumn?: string;
}

/**
 * Start a CSV import job. The job runs async in the backend and completion is
 * normally signalled via `AUDIENCE_IMPORT_COMPLETED` socket event, which the
 * `useAudienceImportSocket` hook picks up and invalidates caches.
 *
 * For small CSVs the job may complete BEFORE the socket listener is registered,
 * so we also schedule a safety-net refetch after a short delay to guarantee
 * the UI eventually shows the imported entries.
 */
export const useStartImport = (
  audienceId: string | undefined
): UseMutationResult<AudienceImportResponse, Error, StartImportArgs> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, identifierColumn, nameColumn }: StartImportArgs) =>
      audiencesService.startImport(audienceId!, file, identifierColumn, nameColumn),
    onSuccess: () => {
      if (!audienceId) return;
      // Safety-net: refetch entries and the audience document shortly after,
      // in case the socket COMPLETED event fired before the listener was ready.
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['audience-entries', audienceId] });
        queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
        queryClient.invalidateQueries({ queryKey: ['audiences'] });
      };
      // Immediate invalidation — covers the case where the job already finished
      invalidate();
      // Delayed retries — covers the case where the job is still running
      setTimeout(invalidate, 1500);
      setTimeout(invalidate, 4000);
    },
  });
};
